import React, { useEffect, useRef, useState } from 'react';
import { theme } from 'styles/theme';

/**
 * 그리드 우클릭 컨텍스트 메뉴
 * items: { id, label, shortcut?, disabled?, danger?, info?, separator?, children?: same[] }
 */
const GridContextMenu = ({ x, y, items, onSelect, onClose }) => {
  const ref = useRef(null);
  const [openSubId, setOpenSubId] = useState(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - rect.width - 8);
    if (top + rect.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - rect.height - 8);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [x, y, items]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 10050,
        minWidth: 200,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius?.sm || 4,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        padding: '4px 0',
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.text.primary,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, idx) => {
        if (item.separator) {
          return (
            <div
              key={`sep-${idx}`}
              style={{
                height: 1,
                margin: '4px 0',
                background: theme.colors.borderLight || theme.colors.border,
              }}
            />
          );
        }

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const subOpen = openSubId === item.id;

        return (
          <div
            key={item.id}
            style={{ position: 'relative' }}
            onMouseEnter={() => {
              if (hasChildren && !item.disabled) setOpenSubId(item.id);
              else setOpenSubId(null);
            }}
          >
            <button
              type="button"
              role="menuitem"
              disabled={item.disabled || item.info}
              onClick={() => {
                if (item.disabled || item.info) return;
                if (hasChildren) {
                  setOpenSubId((prev) => (prev === item.id ? null : item.id));
                  return;
                }
                onSelect(item.id);
              }}
              style={menuItemStyle(item)}
              onMouseEnter={(e) => {
                if (!item.disabled && !item.info) e.currentTarget.style.background = theme.colors.primaryLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.info
                  ? theme.colors.background.light
                  : 'transparent';
              }}
            >
              <span>{item.label}</span>
              {hasChildren ? (
                <span style={{ color: theme.colors.text.tertiary, fontSize: 10 }}>▶</span>
              ) : item.shortcut ? (
                <span style={{ color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.xs }}>
                  {item.shortcut}
                </span>
              ) : null}
            </button>
            {hasChildren && subOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  marginLeft: 2,
                  minWidth: 160,
                  background: theme.colors.white,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius?.sm || 4,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                  padding: '4px 0',
                  zIndex: 1,
                }}
              >
                {item.children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    role="menuitem"
                    disabled={child.disabled}
                    onClick={() => {
                      if (child.disabled) return;
                      onSelect(child.id);
                    }}
                    style={menuItemStyle(child)}
                    onMouseEnter={(e) => {
                      if (!child.disabled) e.currentTarget.style.background = theme.colors.primaryLight;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span>{child.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function menuItemStyle(item) {
  return {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '6px 12px',
    border: 'none',
    background: item.info ? theme.colors.background.light : 'transparent',
    cursor: item.disabled || item.info ? 'default' : 'pointer',
    color: item.disabled
      ? theme.colors.text.tertiary
      : item.danger
        ? theme.colors.status.dangerText
        : item.info
          ? theme.colors.text.secondary
          : theme.colors.text.primary,
    fontSize: item.info ? theme.typography.fontSize.xs : 'inherit',
    fontVariantNumeric: item.info ? 'tabular-nums' : undefined,
    textAlign: 'left',
  };
}

export default GridContextMenu;
